<template>
  <div class="discovery-feed">
    <transition mode="in-out" name="fade">
      <div v-if="showNewPost" class="notify notify--success-top-center">
        <h4 class="underline" @click="handleNewPostClick">new posts!</h4>
      </div>
    </transition>

    <div class="page-container">
      <bulletin-button
        v-if="show_create_post_button"
        class="create-post-button"
        :to="'/plus/create-post'"
        variant="primary"
        tag="button"
      >
        create post
      </bulletin-button>

      <h4 class="page-title">DISCOVERY FEED</h4>
      <div class="feed-filters">
        <div class="feed-filter" :class="{ active: activeFilter === 'ALL' }" @click="switchToAllPosts()">All</div>
        <div
          class="feed-filter"
          :class="{ active: activeFilter === 'FOLLOW' }"
          @click="switchToFollowedBrandsPosts()"
          v-if="showBrandsYouFollow"
        >
          Brands You Follow
        </div>
      </div>

      <div v-if="posts && $data && $data.feed_events && !loadingOnSwitch">
        <discovery-feed-post
          v-for="(post, index) in getPosts()"
          :post="post"
          :visibility-changed="(isVisible, entry) => visibilityChanged(isVisible, entry, index)"
          :key="post.node.id"
          :feed-events="$data.feed_events"
        ></discovery-feed-post>
      </div>

      <div v-else class="loading-screen">
        <img alt="bulletin logo" class="loadingImage" src="@/assets/img/bulletin-admin-thumbnail.png" />
      </div>

      <div v-if="showLoadMoreButton" class="load-more-container">
        <bulletin-button v-if="!show_loader" @click="loadMorePosts"> load more </bulletin-button>
      </div>

      <div class="loader-container">
        <loader-static v-if="show_loader" class="loader" />
      </div>

      <div
        class="followed-brands-empty-state"
        v-if="activeFilter === 'FOLLOW' && noFavoritePosts() && !loadingOnSwitch"
      >
        <h2 class="font-size-32">You're not following any brands.</h2>
        <bulletin-button to="/brands" class="margin-top-24">Browse brands</bulletin-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/sass/base/_vars.scss";
@import "@/assets/sass/base/_mixins.scss";

.discovery-feed {
  background-color: $color-light-grey;
}

.page-container {
  padding: 24px;
  background-color: white;
  max-width: 874px;
  margin: 0 auto;

  @include respond-to("small") {
    padding: 16px;
  }
}

.create-post-button {
  margin-bottom: 24px;

  @include respond-to("small") {
    width: 100% !important;
    margin-top: 8px;
  }
}

.page-title {
  font-size: 16px;
}

.feed-filters {
  display: flex;
  margin-top: 16px;

  .feed-filter {
    height: 32px;
    line-height: 30px;
    padding-inline: 16px;
    border-radius: 16px;
    background: $color-light-grey;
    border: 1px solid $secondaryGrey;
    margin-right: 8px;
    font-family: NHaasGroteskDSPro-55Rg;
    font-size: 16px;

    &.active {
      background: $color-green;
      border-color: $color-green;
    }

    &:hover {
      cursor: pointer;
    }
  }
}

.load-more-container,
.loader-container {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.loading-screen {
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  .loadingImage {
    height: 50px;
    width: 50px;
    margin: 0 auto;
    animation-name: stretch;
    animation-duration: 1.2s;
    animation-timing-function: ease-out;
    animation-delay: 0s;
    animation-direction: alternate;
    animation-iteration-count: infinite;
    animation-fill-mode: none;
    animation-play-state: running;
  }
  @keyframes stretch {
    0% {
      transform: scale(0.9);
      opacity: 0.3;
      border-radius: 100%;
    }
    50% {
      // opacity:1.1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
}

.followed-brands-empty-state {
  min-height: calc(100vh - 200px);
  padding-top: 40px;
  margin-top: 24px;
  border-top: 1px solid $secondaryGrey;
}
</style>

<script>
/* eslint-disable no-underscore-dangle */
import InlineSvg from "vue-inline-svg";
import FeedEvents from "@/services/feed/events";
import Vue from "vue";
import { ObserveVisibility } from "vue-observe-visibility";
import FollowButton from "./TheFollowButton.vue";
import PostCaption from "./PostCaption.vue";
import ChatButtonById from "../../components/shared/chatButtonById/chatButtonById.vue";
import { mapGetters } from "vuex";
import FavoritesApi from "../../services/api/favoritesApi";
import Pusher from "pusher-js";
import Env from "../../env.js";
import PostImageWithProductCarousel from "@/views/TheDiscoveryFeed/PostImageWithProductCarousel.vue";
import PostActions from "@/views/TheDiscoveryFeed/PostActions";
import BrandValuesList from "@/views/TheDiscoveryFeed/BrandValuesList";
import PostLink from "@/views/TheDiscoveryFeed/PostLink";
import BrandInfo from "./BrandInfo";
import PostContent from "./PostContent";
import DiscoveryFeedPost from "./DiscoveryFeedPost";
import { POSTS_QUERY } from "./postsQuery";

const FavoritesApiService = new FavoritesApi();

Vue.directive("observe-visibility", ObserveVisibility);

export default {
  components: {
    ChatButtonById,
    PostImageWithProductCarousel,
    InlineSvg,
    FollowButton,
    PostCaption,
    PostActions,
    PostLink,
    BrandValuesList,
    BrandInfo,
    PostContent,
    DiscoveryFeedPost,
  },
  apollo: {
    posts: {
      query: POSTS_QUERY,
      variables: {
        cursor: "",
      },
    },
  },
  data() {
    return {
      isRetailer: false,
      show_create_post_button: false,
      posts: null,
      show_loader: false,
      buffered_events: [],
      feed_events: undefined,
      showFixedButton: false,
      showNewPost: false,
      activeFilter: "ALL",
      favoritePosts: null,
      loadingOnSwitch: false,
    };
  },
  methods: {
    checkAccess,
    fetchMorePosts,
    wantPost,
    unWantPost,
    visibilityChanged,
    viewedAPost,
    handleScroll,
    handlebrandImageClick,
    handlebrandNameClick,
    redirectToBrandPage,
    subscribeToNewPost,
    handleNewPostClick,
    getFavoritePosts,
    getPosts,
    switchToFollowedBrandsPosts,
    switchToAllPosts,
    noFavoritePosts,
    loadMorePosts,
  },

  async beforeMount() {
    this.isRetailer = this.$session.getUserInfo().role === "RETAILER";
    this.show_create_post_button = !this.isRetailer;

    if (this.isRetailer) {
      const { data } = await FavoritesApiService.getFavoriteBrands();
      this.$store.commit("setFavoriteState", data.brand_ids);
      this.$utils.getFavoriteProducts.call(this);
    }
  },

  created() {
    this.$data.feed_events = new FeedEvents(this);
    window.addEventListener("scroll", this.handleScroll);
  },

  beforeDestroy() {
    this.$eventBus.$off("accessChange", this.checkAccess);
  },

  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
  },

  mounted() {
    // do a refetch if we are returning from a create post
    if (this.posts) {
      this.$apollo.queries.posts.refetch();
    }

    setTimeout(
      function () {
        this.subscribeToNewPost();
        // eslint-disable-next-line no-magic-numbers
      }.bind(this),
      3000
    );

    this.$ma.trackEvent({
      action: "Bulletin Plus Page View",
    });

    this.$eventBus.$on("accessChange", this.checkAccess);
  },

  computed: {
    isRetailerApproved() {
      return this.$store.getters.isRetailerApproved;
    },
    ...mapGetters({
      user: "userState",
      favoriteBrandIds: "favoritesState",
      plusAccess: "plusAccessState",
    }),
    showLoadMoreButton() {
      if (this.activeFilter === "ALL") {
        return this.posts && this.posts.pageInfo.hasNextPage;
      }
      if (this.activeFilter === "FOLLOW") {
        return this.favoritePosts && this.favoritePosts.pageInfo.hasNextPage;
      }

      return false;
    },
    showBrandsYouFollow() {
      return this.user?.role === "RETAILER";
    },
  },

  watch: {
    user: function () {
      this.checkAccess();
    },
  },
};

function checkAccess() {
  if (this.user.role === "BRAND" && !this.plusAccess) {
    this.$router.replace({
      path: "/",
      query: {
        redirect: "plus",
      },
    });
  }
}

function fetchMorePosts(cursor) {
  const pos = window.scrollY;
  this.show_loader = true;
  this.$apollo.queries.posts
    .fetchMore({
      variables: {
        cursor: cursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        return {
          posts: {
            __typename: previousResult.posts.__typename,
            edges: [...previousResult.posts.edges, ...fetchMoreResult.posts.edges],
            pageInfo: fetchMoreResult.posts.pageInfo,
          },
        };
      },
    })
    .then(() => {
      scrollTo(0, pos);
      this.show_loader = false;
    });
}

function wantPost(index) {
  const post = this.$data.posts.edges[index].node;
  post.isLiked = true;

  this.$data.feed_events.track("LIKE", post, index);
}

function unWantPost(index) {
  const post = this.$data.posts.edges[index].node;
  post.isLiked = false;

  this.$data.feed_events.track("DISLIKE", post, index);
}

function viewedAPost(post, index) {
  this.$data.feed_events.track("VIEW", post, index);
}

function visibilityChanged(isVisible, elem, index) {
  if (isVisible) {
    const post =
      this.activeFilter === "FOLLOW" ? this.favoritePosts.edges[index].node : this.$data.posts.edges[index].node;
    this.viewedAPost(post, index);
  }
}
function handleScroll() {
  const scrollPXheight = 85;
  this.showFixedButton = window.scrollY > scrollPXheight;
}
function handlebrandImageClick(id) {
  this.redirectToBrandPage(id);
}
function handlebrandNameClick(id) {
  this.redirectToBrandPage(id);
}
function redirectToBrandPage(vanityUrlName) {
  this.$router.push("/brands/" + vanityUrlName);
}

function subscribeToNewPost() {
  const pusherOptions = Env.pusherOptions(),
    pusher = new Pusher(pusherOptions.key, pusherOptions.options);

  pusher.connection.bind("error", function (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  });
  pusher.subscribe("public-bulletinplus");
  pusher.bind("new-post", () => {
    this.showNewPost = true;
  });
}

function handleNewPostClick() {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
  this.$apollo.queries.posts.refetch();
  this.showNewPost = false;
}

async function getFavoritePosts(endCursor) {
  try {
    if (endCursor) {
      this.show_loader = true;
    } else {
      this.loadingOnSwitch = true;
    }

    const { data } = await this.$apollo.query({
      query: POSTS_QUERY,
      errorPolicy: "all",
      variables: {
        after: "",
        before: "",
        cursor: endCursor || "",
        filters: {
          or: [
            ...this.favoriteBrandIds.map((brandId) => {
              return { brandId };
            }),
          ],
        },
      },
    });

    const previousPosts = this.favoritePosts?.edges || [];

    this.favoritePosts = { edges: [...previousPosts, ...(data.posts?.edges || [])], pageInfo: data?.posts?.pageInfo };
  } catch (e) {
    this.$logger.notify(new Error(error));
  } finally {
    this.show_loader = false;
    this.loadingOnSwitch = false;
  }
}

function getPosts() {
  if (this.activeFilter === "ALL") {
    return this.posts.edges;
  }
  return this.favoritePosts?.edges || [];
}

function switchToFollowedBrandsPosts() {
  if (!this.favoriteBrandIds) {
    return;
  }

  this.activeFilter = "FOLLOW";

  if (!this.favoriteBrandIds.length) {
    this.favoritePosts = null;
    return;
  }

  this.getFavoritePosts();
}

function noFavoritePosts() {
  return !this.favoritePosts || !this.favoritePosts?.edges?.length;
}

function switchToAllPosts() {
  this.activeFilter = "ALL";
  this.favoritePosts = null;
}

function loadMorePosts() {
  if (this.activeFilter === "ALL") {
    this.fetchMorePosts(this.posts.pageInfo.endCursor);
  } else if (this.activeFilter === "FOLLOW") {
    this.getFavoritePosts(this.favoritePosts.pageInfo.endCursor);
  }
}
</script>
